import React, { useState, useEffect } from "react";
import axios from "axios";
import styled from "styled-components";

const url_t = process.env.REACT_APP_GLOBAL_URL;

const ProfileV2 = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [formData, setFormData] = useState({
    userId: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    bio: "",
    phone: "",
    address: "",
    nation: "",
    gender: "",
    language: "",
    profile_image: "",
  });

  const sessionId = localStorage.getItem("sessionId");

  // Robustly fetch user credentials (use numeric user id fallback when needed)
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // try to get numeric fallback from loginData or explicit userId stored elsewhere
        const rawLoginData = localStorage.getItem("loginData");
        let fallbackId = null;
        if (rawLoginData) {
          try {
            const parsed = JSON.parse(rawLoginData);
            if (parsed?.id) fallbackId = String(parsed.id);
          } catch (e) {
            // ignore parse errors
          }
        }

        // If sessionId looks like a number, use it; otherwise prefer fallbackId
        const profileLookupId = /^\d+$/.test(sessionId || "") ? sessionId : fallbackId;

        if (!profileLookupId) return;

        const res = await axios.get(`${url_t}auth/user_profile`, {
          headers: { "session-id": String(profileLookupId) },
        });

        const u = res.data.user;
        // ensure id is numeric when available
        u.id = u.id || profileLookupId;
        setUser(u);
        setFormData({
          userId: u.id || "",
          firstname: u.firstname || "",
          lastname: u.lastname || "",
          email: u.email || "",
          password: "",
          bio: u.bio || "",
          phone: u.phone || "",
          address: u.address || "",
          nation: u.nation || "",
          gender: u.gender || "",
          language: u.language || "",
          profile_image: u.profile_image || "",
        });
        setImagePreview(
          u.profile_image
            ? `${url_t}${u.profile_image}`
            : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
        );
      } catch (err) {
        console.error("❌ Error fetching profile:", err);
      }
    };

    fetchProfile();
  }, [sessionId]);

  // ✅ Handle input text change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    setFormData((prev) => ({ ...prev, profile_image: file }));
  };

  // ✅ Save profile
  const handleSave = async () => {
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "profile_image" && value instanceof File) {
          formDataToSend.append("profile_image", value);
        } else {
          formDataToSend.append(key, value);
        }
      });
      formDataToSend.append("sessionId", sessionId);

      await axios.put(`${url_t}auth/update_profile`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("✅ Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      console.error("❌ Update failed:", err);
      alert("Failed to update profile");
    }
  };

  if (!user) return <Loader>Loading profile...</Loader>;

  return (
    <Container>
      <Card>
        <ProfileImageWrapper>
          <ProfileImage src={imagePreview} alt="Profile" />
          {isEditing && (
            <>
              <FileInputLabel htmlFor="profileImageUpload">
                Change Photo
              </FileInputLabel>
              <FileInput
                id="profileImageUpload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </>
          )}
        </ProfileImageWrapper>

        <Name>{formData.firstname} {formData.lastname}</Name>

        <Form>
          <Field>
            <Label>User ID</Label>
            <Input name="userId" value={formData.userId} disabled />
          </Field>

          <Field>
            <Label>Firstname</Label>
            <Input
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Lastname</Label>
            <Input
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Email</Label>
            <Input
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Password</Label>
            <Input
              type="password"
              name="password"
              placeholder="Enter new password"
              value={formData.password}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Bio</Label>
            <TextArea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Phone</Label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Address</Label>
            <Input
              name="address"
              value={formData.address}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Nation</Label>
            <Input
              name="nation"
              value={formData.nation}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </Field>

          <Field>
            <Label>Gender</Label>
            <Select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              disabled={!isEditing}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </Select>
          </Field>

          <Field>
            <Label>Language</Label>
            <Select
              name="language"
              value={formData.language}
              onChange={handleChange}
              disabled={!isEditing}
            >
              <option value="">Select</option>
              <option value="English">English</option>
              <option value="Filipino">Filipino</option>
              <option value="Bisaya">Bisaya</option>
            </Select>
          </Field>

          {isEditing ? (
            <SaveButton onClick={handleSave}>Save Changes</SaveButton>
          ) : (
            <EditButton onClick={() => setIsEditing(true)}>Edit</EditButton>
          )}
        </Form>
      </Card>
    </Container>
  );
};

export default ProfileV2;

// 🧱 Improved Profile Layout (Wider, Balanced, Better Visual Hierarchy)
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 1rem;
  background: #f8fafc;
  min-height: 90vh;
`;

const Card = styled.div`
  width: 100%;
  max-width: 800px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 2.5rem 3rem;
  text-align: center;
  transition: all 0.3s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ProfileImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const ProfileImage = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 50%;
  margin-bottom: 0.8rem;
  object-fit: cover;
  border: 3px solid #e2e8f0;
`;

const FileInputLabel = styled.label`
  background: #2563eb;
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: 0.2s;
  &:hover {
    background: #1d4ed8;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const Name = styled.h2`
  font-size: 1.4rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.2rem;
`;

const Form = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem 1.5rem;
  text-align: left;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;

  &.wide {
    grid-column: span 2;

    @media (max-width: 600px) {
      grid-column: span 1;
    }
  }
`;

const Label = styled.label`
  color: #475569;
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  outline: none;
  font-size: 0.9rem;
  transition: border-color 0.2s;

  &:focus {
    border-color: #3b82f6;
  }

  &:disabled {
    background: #f1f5f9;
  }
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  resize: none;
  min-height: 60px;
  font-size: 0.9rem;
  &:disabled {
    background: #f1f5f9;
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 0.9rem;
  &:disabled {
    background: #f1f5f9;
  }
`;

const EditButton = styled.button`
  margin-top: 1.5rem;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.7rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  grid-column: span 2;
  transition: all 0.2s;

  &:hover {
    background: #1d4ed8;
  }
`;

const SaveButton = styled(EditButton)`
  background: #10b981;
  &:hover {
    background: #059669;
  }
`;

const Loader = styled.div`
  text-align: center;
  font-size: 1rem;
  color: #475569;
  padding: 2rem;
`;
